import { useReducer } from 'react';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import addDays from 'date-fns/addDays';
import isWithinRange from 'date-fns/isWithinInterval';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameDay';
import {
  isDateSelected as isDateSelectedFn,
  isDateBlocked as isDateBlockedFn,
  isFirstOrLastSelectedDate as isFirstOrLastSelectedDateFn,
  isEndDate as isEndDateFn,
  isStartDate as isStartDateFn,
  canSelectRange,
  isDateHovered as isDateHoveredFn,
  isInUnavailableDates
} from './useDatePicker.utils';
import { isInUnavailableFetchDates } from './../useBlackoutDates/util';
import { BlackoutDates } from './../../../../containers/VerticalBookingContainer/blackoutDates';
import { useDatePickerI18n } from './../../datePickerI18n';
import { formatDate } from '../../../../../components/lab/utils';
import { createInitialDatePickerState, datePickerReducer } from './useDatePickerReducer';

export const START_DATE = 'startDate';
export const END_DATE = 'endDate';

export type FocusedInput = 'startDate' | 'endDate' | null;

export interface OnDatesChangeProps {
  focusedInput: FocusedInput;
  selectPhase: FocusedInput;
  startDate: Date | null;
  endDate: Date | null;
}

export type FirstDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface UseDatePickerProps {
  onDatesChange(data: OnDatesChangeProps): void;
  minBookingDate?: Date;
  maxBookingDate?: Date;
  startDate: Date | null;
  endDate: Date | null;
  focusedInput: FocusedInput;
  selectPhase: FocusedInput;
  numberOfMonths?: number;
  minBookingDays?: number;
  exactMinBookingDays?: boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  initialVisibleMonth?: Date;
  isDateBlocked?(date: Date): boolean;
  unavailableDates?: Date[];
  fetchUnavailableDates?: BlackoutDates | null;
  blackoutDatesIntegration?: boolean;
}

export function useDatePicker({
  startDate,
  endDate,
  focusedInput,
  selectPhase,
  minBookingDate,
  maxBookingDate,
  onDatesChange,
  initialVisibleMonth,
  exactMinBookingDays = false,
  minBookingDays = 1,
  numberOfMonths = 2,
  firstDayOfWeek = 1,
  isDateBlocked: isDateBlockedProps = () => false,
  unavailableDates = [],
  fetchUnavailableDates = null,
  blackoutDatesIntegration = false
}: UseDatePickerProps) {
  const [state, dispatch] = useReducer(
    datePickerReducer,
    createInitialDatePickerState(numberOfMonths, startDate, initialVisibleMonth)
  );
  const { format = formatDate } = useDatePickerI18n();

  const { activeMonths, hoveredDate, focusedDate } = state;

  const disabledDatesByUser = (date: Date) => {
    return (
      isInUnavailableDates(unavailableDates, date) ||
      isDateBlockedProps(date) ||
      isInUnavailableFetchDates(fetchUnavailableDates, date, selectPhase, format)
    );
  };

  const onDateFocus = (date: Date) => {
    if (!focusedDate || (focusedDate && !isSameMonth(date, focusedDate))) {
      dispatch({
        type: 'FOCUS_DATE_WITH_MONTHS_UPDATE',
        payload: { date, numberOfMonths, currentActiveMonths: activeMonths }
      });
    } else {
      dispatch({ type: 'SET_FOCUSED_DATE', payload: date });
    }
  };

  const isDateSelected = (date: Date) => isDateSelectedFn(date, startDate, endDate);

  const isFirstOrLastSelectedDate = (date: Date) =>
    isFirstOrLastSelectedDateFn(date, startDate, endDate);

  const isStartDate = (date: Date) => isStartDateFn(date, startDate);

  const isEndDate = (date: Date) => isEndDateFn(date, endDate);

  const isDateBlocked = (date: Date) =>
    isDateBlockedFn({
      date,
      minBookingDate,
      maxBookingDate,
      startDate,
      endDate,
      minBookingDays,
      isDateBlockedFn: disabledDatesByUser
    });

  const isDateFocused = (date: Date) =>
    focusedDate ? isSameDay(date, focusedDate) && !disabledDatesByUser(date) : false;

  const isDateHovered = (date: Date) =>
    isDateHoveredFn({
      date,
      hoveredDate,
      startDate,
      endDate,
      minBookingDays,
      exactMinBookingDays,
      isDateBlocked: disabledDatesByUser
    });

  function onResetDates() {
    onDatesChange({
      startDate: null,
      endDate: null,
      focusedInput: START_DATE,
      selectPhase: START_DATE
    });
  }

  function resetDatesKeepingFocus() {
    onDatesChange({
      startDate: null,
      endDate: null,
      focusedInput,
      selectPhase: START_DATE
    });
  }

  function onDateSelect(date: Date, changeFocus: boolean = true) {
    if (
      (selectPhase === END_DATE || selectPhase === START_DATE) &&
      minBookingDays > 0 &&
      exactMinBookingDays &&
      canSelectRange({
        minBookingDays,
        exactMinBookingDays,
        minBookingDate,
        maxBookingDate,
        isDateBlocked: disabledDatesByUser,
        startDate: date,
        endDate: null,
        blackoutDatesIntegration
      })
    ) {
      onDatesChange({
        startDate: date,
        endDate: addDays(date, minBookingDays - 1),
        focusedInput: changeFocus ? null : focusedInput,
        selectPhase: null
      });
    } else if (
      ((selectPhase === END_DATE && startDate && isBefore(date, startDate)) ||
        (selectPhase === START_DATE && endDate && isAfter(date, endDate))) &&
      !exactMinBookingDays &&
      canSelectRange({
        minBookingDays,
        isDateBlocked: disabledDatesByUser,
        startDate: date,
        endDate: null,
        blackoutDatesIntegration
      })
    ) {
      onDatesChange({
        endDate: null,
        startDate: date,
        focusedInput: changeFocus ? END_DATE : focusedInput,
        selectPhase: END_DATE
      });
    } else if (
      selectPhase === START_DATE &&
      !exactMinBookingDays &&
      canSelectRange({
        minBookingDays,
        isDateBlocked: disabledDatesByUser,
        endDate,
        startDate: date,
        blackoutDatesIntegration
      })
    ) {
      onDatesChange({
        endDate,
        startDate: date,
        focusedInput: changeFocus ? END_DATE : focusedInput,
        selectPhase: END_DATE
      });
    } else if (
      selectPhase === START_DATE &&
      !exactMinBookingDays &&
      canSelectRange({
        minBookingDays,
        isDateBlocked: disabledDatesByUser,
        endDate: null,
        startDate: date,
        blackoutDatesIntegration
      })
    ) {
      onDatesChange({
        endDate: null,
        startDate: date,
        focusedInput: changeFocus ? END_DATE : focusedInput,
        selectPhase: END_DATE
      });
    } else if (
      selectPhase === END_DATE &&
      startDate &&
      !isBefore(date, startDate) &&
      !exactMinBookingDays &&
      canSelectRange({
        minBookingDays,
        isDateBlocked: disabledDatesByUser,
        startDate,
        endDate: date,
        blackoutDatesIntegration
      })
    ) {
      onDatesChange({
        startDate,
        endDate: date,
        focusedInput: changeFocus ? null : focusedInput,
        selectPhase: null
      });
    } else if (selectPhase === END_DATE && !startDate && !exactMinBookingDays) {
      onDatesChange({
        startDate: null,
        endDate: date,
        focusedInput: changeFocus ? null : focusedInput,
        selectPhase: START_DATE
      });
    }
  }

  function onDateHover(date: Date | null) {
    if (!date) {
      dispatch({ type: 'SET_HOVERED_DATE', payload: null });
    } else if (date) {
      const isNotBlocked = !isDateBlocked(date) || (startDate && isSameDay(date, startDate));
      const isHoveredDateAfterOrEqualMinDate = minBookingDate
        ? !isBefore(date, addDays(minBookingDate, -1))
        : true;
      const isHoveredDateBeforeOrEqualMaxDate = maxBookingDate
        ? !isAfter(date, maxBookingDate)
        : true;

      // Exact minimal booking days
      const potentialEndDate = addDays(date, minBookingDays - 1);
      const isPotentialEndDateAfterOrEqualMinDate = minBookingDate
        ? !isBefore(potentialEndDate, minBookingDate)
        : true;
      const isPotentialEndDateBeforeOrEqualMaxDate = maxBookingDate
        ? !isAfter(potentialEndDate, maxBookingDate)
        : true;
      const isExactAndInRange =
        exactMinBookingDays &&
        minBookingDays > 1 &&
        isHoveredDateAfterOrEqualMinDate &&
        isHoveredDateBeforeOrEqualMaxDate &&
        isPotentialEndDateAfterOrEqualMinDate &&
        isPotentialEndDateBeforeOrEqualMaxDate;

      // Is date in range
      const isInRange =
        startDate &&
        !endDate &&
        !exactMinBookingDays &&
        isHoveredDateAfterOrEqualMinDate &&
        isHoveredDateBeforeOrEqualMaxDate;

      // Is start date hovered and in range
      const isMinBookingDaysInRange =
        minBookingDays > 1 && startDate
          ? isWithinRange(date, {
              start: startDate,
              end: addDays(startDate, minBookingDays - 2)
            })
          : true;
      const isStartDateHoveredAndInRange =
        startDate && isSameDay(date, startDate) && isMinBookingDaysInRange;

      if (isNotBlocked && (isExactAndInRange || isInRange || isStartDateHoveredAndInRange)) {
        dispatch({ type: 'SET_HOVERED_DATE', payload: date });
      } else if (hoveredDate !== null) {
        dispatch({ type: 'SET_HOVERED_DATE', payload: null });
      }
    }
  }

  function goToPreviousMonths() {
    dispatch({ type: 'GO_TO_PREVIOUS_MONTHS', payload: { numberOfMonths } });
  }

  function goToPreviousMonthsByOneMonth() {
    dispatch({ type: 'GO_TO_PREVIOUS_MONTHS_BY_ONE', payload: { numberOfMonths } });
  }

  function goToNextMonths() {
    dispatch({ type: 'GO_TO_NEXT_MONTHS', payload: { numberOfMonths } });
  }

  function goToNextMonthsByOneMonth() {
    dispatch({ type: 'GO_TO_NEXT_MONTHS_BY_ONE', payload: { numberOfMonths } });
  }

  function goToDate(date: Date) {
    dispatch({ type: 'GO_TO_DATE', payload: { date, numberOfMonths } });
  }

  function goToPreviousYear(numYears: number = 1) {
    dispatch({ type: 'GO_TO_PREVIOUS_YEAR', payload: { numberOfMonths, numYears } });
  }

  function goToNextYear(numYears: number = 1) {
    dispatch({ type: 'GO_TO_NEXT_YEAR', payload: { numberOfMonths, numYears } });
  }

  const getMinimumEnabledDate = (date: Date, diff: number = 1) => {
    let lastEnableDay = date;
    while (
      isDateBlocked(lastEnableDay) &&
      minBookingDate &&
      isAfter(lastEnableDay, minBookingDate) &&
      maxBookingDate &&
      isBefore(lastEnableDay, maxBookingDate)
    ) {
      lastEnableDay = addDays(lastEnableDay, diff);
    }
    if (minBookingDate && isBefore(lastEnableDay, minBookingDate)) {
      return minBookingDate;
    } else if (maxBookingDate && isAfter(lastEnableDay, maxBookingDate)) {
      return maxBookingDate;
    }
    return lastEnableDay;
  };

  return {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isStartDate,
    isEndDate,
    isDateBlocked,
    numberOfMonths,
    isDateFocused,
    focusedDate,
    hoveredDate,
    onResetDates,
    resetDatesKeepingFocus,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToPreviousMonths,
    goToPreviousMonthsByOneMonth,
    goToNextMonths,
    goToNextMonthsByOneMonth,
    goToDate,
    goToPreviousYear,
    goToNextYear,
    getMinimumEnabledDate
  };
}
